import { useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { FormRow } from "@/components/admin/form";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { fmtFCFA } from "@/lib/format";
import {
	useConfirmBankTransfer,
	useExtendReservation,
} from "@/lib/orders/orders-hooks";
import type { Order } from "@/lib/orders/types";

interface BankTransferModalProps {
	order: Order;
	onClose: () => void;
}

/**
 * Encaissement d'un virement.
 *
 * L'API refuse (400) un montant différent du total tant que l'écart n'est pas
 * reconnu explicitement. Ce refus n'est pas une erreur à masquer : c'est le
 * garde-fou qui évite d'expédier une commande à moitié réglée. On affiche donc
 * le message tel qu'il arrive, et on ne propose de passer outre que lorsque
 * l'écart est bien la cause — un virement déjà confirmé, lui, ne se force pas.
 */
export function ConfirmBankTransferModal({
	order,
	onClose,
}: BankTransferModalProps) {
	const toast = useToast();
	const confirmer = useConfirmBankTransfer(order.id);
	const [montant, setMontant] = useState<number | "">(order.totalAmount);
	const [dateValeur, setDateValeur] = useState("");
	const [reference, setReference] = useState(order.paymentReference ?? "");
	const [erreur, setErreur] = useState<string | null>(null);

	const montantNum = montant === "" ? 0 : Number(montant);
	const ecart = montantNum - order.totalAmount;

	const envoyer = async (accepterEcart: boolean) => {
		setErreur(null);
		if (montantNum < 1) {
			setErreur("Saisissez le montant réellement reçu sur le compte.");
			return;
		}
		try {
			await confirmer.mutateAsync({
				amount: montantNum,
				valueDate: dateValeur || undefined,
				reference: reference.trim() || undefined,
				acceptGap: accepterEcart || undefined,
			});
			toast.success(`Virement de ${fmtFCFA(montantNum)} confirmé.`);
			onClose();
		} catch (e) {
			setErreur(extractErrorMessage(e, "Confirmation impossible"));
		}
	};

	return (
		<Modal
			title="Confirmer le virement reçu"
			subtitle={
				<>
					Commande <span className="mono">{order.number}</span> · total attendu{" "}
					<strong>{fmtFCFA(order.totalAmount)}</strong>
				</>
			}
			onClose={onClose}
			width={480}
		>
			<FormRow label="Montant reçu (FCFA)" required>
				<input
					className="aform-i"
					type="number"
					min={1}
					value={montant}
					onChange={(e) =>
						setMontant(e.target.value === "" ? "" : Number(e.target.value))
					}
					data-testid="bank-transfer-amount"
				/>
			</FormRow>
			{ecart !== 0 && montant !== "" && (
				<div
					style={{
						marginTop: -6,
						marginBottom: 14,
						fontSize: 12,
						color: "var(--a-warning, #c98d10)",
					}}
				>
					Écart de {ecart > 0 ? "+" : "−"}
					{fmtFCFA(Math.abs(ecart))} par rapport au total.
				</div>
			)}
			<FormRow label="Date de valeur" helper="Date de crédit sur le compte">
				<input
					className="aform-i"
					type="date"
					value={dateValeur}
					onChange={(e) => setDateValeur(e.target.value)}
				/>
			</FormRow>
			<FormRow label="Référence bancaire" helper="Libellé du relevé">
				<input
					className="aform-i"
					value={reference}
					onChange={(e) => setReference(e.target.value)}
					placeholder={order.number}
				/>
			</FormRow>

			{erreur && (
				<div
					style={{
						padding: "10px 12px",
						borderRadius: 6,
						background: "var(--a-cream, #faf6ef)",
						border: "1px solid var(--a-danger, #b23a2a)",
						color: "var(--a-ink)",
						fontSize: 12.5,
						lineHeight: 1.5,
					}}
					data-testid="bank-transfer-error"
				>
					{erreur}
				</div>
			)}

			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose}>
					Annuler
				</AdminButton>
				{erreur && ecart !== 0 && (
					<AdminButton
						variant="secondary"
						disabled={confirmer.isPending}
						onClick={() => envoyer(true)}
						data-testid="bank-transfer-accept-gap"
					>
						Confirmer malgré l'écart
					</AdminButton>
				)}
				<AdminButton
					variant="primary"
					disabled={confirmer.isPending || montant === ""}
					onClick={() => envoyer(false)}
					data-testid="bank-transfer-submit"
				>
					{confirmer.isPending ? "Enregistrement…" : "Confirmer le virement"}
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}

/**
 * Report d'échéance.
 *
 * Le motif est exigé par l'API et vérifié ici aussi : c'est lui qui permettra,
 * des semaines plus tard, de distinguer un client sérieux d'une commande qu'on
 * aurait dû libérer. Sans lui, la liste des prolongations ne dit rien.
 */
export function ExtendReservationModal({
	order,
	onClose,
}: BankTransferModalProps) {
	const toast = useToast();
	const prolonger = useExtendReservation(order.id);
	const [jours, setJours] = useState<number | "">(3);
	const [motif, setMotif] = useState("");
	const [erreur, setErreur] = useState<string | null>(null);

	const joursNum = jours === "" ? 0 : Number(jours);
	const motifValide = motif.trim().length >= 3;

	const envoyer = async () => {
		setErreur(null);
		if (joursNum < 1 || joursNum > 30) {
			setErreur("La prolongation doit tenir entre 1 et 30 jours.");
			return;
		}
		if (!motifValide) {
			setErreur("Indiquez pourquoi la réservation est maintenue (3 caractères minimum).");
			return;
		}
		try {
			await prolonger.mutateAsync({ days: joursNum, reason: motif.trim() });
			toast.success(`Réservation prolongée de ${joursNum} jour(s).`);
			onClose();
		} catch (e) {
			setErreur(extractErrorMessage(e, "Prolongation impossible"));
		}
	};

	return (
		<Modal
			title="Prolonger la réservation"
			subtitle={
				<>
					Commande <span className="mono">{order.number}</span> ·{" "}
					{order.reservationExtensions} prolongation(s) déjà accordée(s)
				</>
			}
			onClose={onClose}
			width={480}
		>
			<FormRow label="Nombre de jours" required helper="1 à 30 jours">
				<input
					className="aform-i"
					type="number"
					min={1}
					max={30}
					value={jours}
					onChange={(e) =>
						setJours(e.target.value === "" ? "" : Number(e.target.value))
					}
					data-testid="extend-days"
				/>
			</FormRow>
			<FormRow
				label="Motif"
				required
				helper="Conservé au journal de la commande"
			>
				<textarea
					className="aform-i"
					rows={3}
					value={motif}
					onChange={(e) => setMotif(e.target.value)}
					placeholder="Ex. : client a envoyé la preuve de virement, crédit attendu lundi."
					data-testid="extend-reason"
				/>
			</FormRow>

			{erreur && (
				<div
					style={{
						padding: "10px 12px",
						borderRadius: 6,
						background: "var(--a-cream, #faf6ef)",
						border: "1px solid var(--a-danger, #b23a2a)",
						color: "var(--a-ink)",
						fontSize: 12.5,
						lineHeight: 1.5,
					}}
				>
					{erreur}
				</div>
			)}

			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose}>
					Annuler
				</AdminButton>
				<AdminButton
					variant="primary"
					disabled={prolonger.isPending || !motifValide || jours === ""}
					onClick={envoyer}
					data-testid="extend-submit"
				>
					{prolonger.isPending ? "Enregistrement…" : "Prolonger"}
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
